//* *Third-pary components & libraries
import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

//* *React Imports
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import DateInput from 'components/field/DateInput';
import IntegerInput from 'components/field/IntegerInput';
import Status from 'components/field/Status';
import StringInput from 'components/field/StringInput';
import TextArea from 'components/field/TextArea';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import MinimizeButton from 'components/global/MinimizeButton';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListData } from 'components/list/dynamicListSlice';

import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';
import useModalRouter from 'hooks/useModalRouter';
import { usePrompt } from 'hooks/usePrompt';

import { AUTOCOMPLETE, PURCHASE_ORDER } from 'services/consts';

import AppLogger from 'utils/AppLogger';
import { DYNAMICLISTS } from 'utils/constants';

import { PurchaseOrderItems } from './PurchaseOrderItems';

export const PurchaseOrderForm = NiceModal.create(({ modalName, mode, id }) => {
	const { closeModal } = useModalRouter();
	const dispatch = useDispatch();
	const [isMinimizing, setIsMinimizing] = useState(false);

	const schema = useMemo(
		() =>
			yup
				.object()
				.shape({
					status: yup.string().nullable(),
					vendor: yup.string().nullable().required(),
					warehouseID: yup.string().nullable().required(),
					RequestedBy: yup.string().nullable().required(),
					requestedByUsername: yup.string().nullable().required(),
					date: yup.string().nullable(),
					receiveDate: yup.string().nullable(),
					shippingTerm: yup.string().nullable(),
					paymentTerm: yup.string().nullable(),
					RefNumber: yup.string().nullable().required(),
					shipDate: yup.string().nullable(),
					cancelDate: yup.string().nullable(),
					freightTerm: yup.string().nullable(),
					taxTotal: yup.number().nullable(),
					discount: yup.number().nullable(),
					shipping: yup.number().nullable(),
					miscCharges: yup.number().nullable(),
					purchaseOrderItems: yup.array().of(
						yup
							.object()
							.shape({
								productID: yup.number().nullable().required(),
								SKU: yup.string().nullable().required(),
								price: yup.number().nullable().required(),
								qty: yup.number().nullable().min(0).required(),
								lineTotal: yup.number().nullable().required()
							})
							.nullable()
					)
				})
				.required(),
		[]
	);

	// States
	const [isLoading, setIsLoading] = useState(false);

	// Custom Hooks
	const modal = useModal();
	const axiosPrivate = useAxiosPrivate();
	const { showSuccess, showError } = useAlert();
	const { ID: userID, fullName } = useSelector((state) => state.user);

	const formMethods = useForm({
		resolver: yupResolver(schema),
		defaultValues: {
			status: 'new',
			vendor: null,
			warehouseID: null,
			RequestedBy: userID,
			requestedByUsername: fullName,
			date: null,
			receiveDate: null,
			shippingTerm: null,
			paymentTerm: null,
			RefNumber: null,
			shipDate: null,
			cancelDate: null,
			freightTerm: null,
			taxTotal: null,
			discount: null,
			shipping: null,
			miscCharges: null,
			purchaseOrderItems: [
				{
					productID: null,
					qty: 1,
					price: null,
					lineTotal: null
				}
			]
		}
	});

	const {
		handleSubmit,
		setValue,
		reset,
		watch,
		formState: { isDirty, isSubmitting, isSubmitSuccessful }
	} = formMethods;

	usePrompt(mode !== 'view' && isDirty && !isSubmitting && !isSubmitSuccessful && !isMinimizing);

	const purchaseOrderItems = watch('purchaseOrderItems');

	const totalQuantityValue = purchaseOrderItems.reduce((prev, cur) => prev + (cur.qty ?? 0), 0);
	const subTotalValue = purchaseOrderItems.reduce((prev, cur) => prev + (cur.lineTotal ?? 0), 0);
	const totalValue =
		subTotalValue +
		(watch('taxTotal') ?? 0) +
		(watch('shipping') ?? 0) +
		(watch('miscCharges') ?? 0) -
		(watch('discount') ?? 0);

	const onSubmit = (data) => {
		setIsLoading(true);
		// Determine the url whether mode is edit or add
		const URL = !id ? PURCHASE_ORDER.ADD_PURCHASE_ORDER : PURCHASE_ORDER.UPDATE_PURCHASE_ORDER;
		const notNullData = Object.fromEntries(Object.entries(data).filter(([_, v]) => v !== null));
		const calculatedData = {
			id,
			...notNullData,
			totalQuantity: totalQuantityValue,
			subTotal: subTotalValue,
			total: totalValue
		};

		// Post data to api
		axiosPrivate
			.post(URL, calculatedData)
			.then((response) => {
				const { status } = response;
				if (status === 200) {
					showSuccess();
					dispatch(updateDynamicListData(DYNAMICLISTS.PURCHASE_ORDERS));
					modal.remove();
				} else {
					AppLogger.error(response);
					showError();
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				showError();
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const fillForm = useCallback((id) => {
		setIsLoading(true);
		axiosPrivate
			.post(PURCHASE_ORDER.GET_PURCHASE_ORDER, { id: Number(id) })
			.then((response) => {
				const { data: responseData, status } = response;
				if (status === 200) {
					// reset form fields
					const { ...rest } = responseData;
					reset({
						// Details
						vendor: rest.vendor ?? null,
						warehouseID: rest.warehouseID ?? null,
						RequestedBy: rest.RequestedBy ?? null,
						requestedByUsername: rest.requestedByUsername ?? null,
						date: rest.date ?? null,
						receiveDate: rest.receiveDate ?? null,
						shippingTerm: rest.shippingTerm ?? null,
						paymentTerm: rest.paymentTerm ?? null,
						RefNumber: rest.RefNumber ?? null,
						shipDate: rest.shipDate ?? null,
						cancelDate: rest.cancelDate ?? null,
						freightTerm: rest.freightTerm ?? null,
						taxTotal: rest.taxTotal ?? null,
						discount: rest.discount ?? null,
						shipping: rest.shipping ?? null,
						miscCharges: rest.miscCharges ?? null,
						status: rest.status ?? 'new',
						purchaseOrderItems: rest.purchaseOrderItems ?? null
					});
				} else {
					AppLogger.error(response);
				}
			})
			.catch((error) => {
				AppLogger.error(error);
				modal.remove();
				showError();
			})
			.finally(() => setIsLoading(false));
	}, []);

	// Statics
	const defaultColSpan = {
		md: 6
	};

	// Effects
	useEffect(() => {
		if (id) fillForm(id);
	}, [id]);

	const vendorAutocompleteColumns = useMemo(() => [{ accessor: 'Name', width: 200 }], []);
	const warehouseAutocompleteColumns = useMemo(() => [{ accessor: 'Name', width: 200 }]);

	const getTitle = () =>
		mode === 'view' ? 'viewPurchaseOrder' : mode === 'edit' ? 'editPurchaseOrder' : 'addPurchaseOrder';

	// Rendering
	return (
		<Modal
			maxWidth="large"
			name={modalName}
			minimizeButton={(show, close) => (
				<MinimizeButton
					onShow={() => {
						show();
						setIsMinimizing(false);
					}}
					onClose={close}
					label={getTitle()}
				/>
			)}
			onMinimize={() => {
				setIsMinimizing(true);
			}}>
			<ModalTitle>
				<Typography variant="p1">{getTitle()}</Typography>
			</ModalTitle>
			<ModalContent style={{ width: '100%' }}>
				<Form formMethods={formMethods}>
					<FormItem colSpan={defaultColSpan} label="vendor" isLoading={isLoading} required={true}>
						<AutoComplete
							name="vendor"
							autoCompleteName={AUTOCOMPLETE.VENDOR}
							columns={vendorAutocompleteColumns}
							selectionLabelAccessor={['Name']}
							disabled={mode === 'view'}
							setValue={(value, isFirstRender) => {
								if (isFirstRender) return;
								setValue(`vendor`, value?.ID ?? null, {
									shouldValidate: true,
									shouldDirty: true
								});
							}}
							value={watch('vendor')}
						/>
					</FormItem>

					<FormItem label="refNumber" colSpan={defaultColSpan} isLoading={isLoading} required={true}>
						<StringInput name="RefNumber" disabled={mode === 'view'} />
					</FormItem>

					<FormItem
						colSpan={defaultColSpan}
						label="warehouse"
						isLoading={isLoading}
						roleName="MultiWarehouseModule"
						required={true}>
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.WAREHOUSES}
							columns={warehouseAutocompleteColumns}
							selectionLabelAccessor={['Name']}
							disabled={mode === 'view'}
							name="warehouseID"
							value={watch('warehouseID')}
							setValue={(value, isFirstRender) => {
								if (isFirstRender) return;
								setValue('warehouseID', value?.ID ?? null, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>

					<FormItem label="requestedBy" colSpan={defaultColSpan} isLoading={isLoading}>
						<StringInput name="requestedByUsername" disabled />
					</FormItem>

					<FormItem label="shipDate" colSpan={defaultColSpan} isLoading={isLoading}>
						<DateInput
							name="shipDate"
							value={watch('shipDate')}
							disabled={mode === 'view'}
							setValue={(value) => {
								setValue('shipDate', value, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>

					<FormItem label="shippingTerm" colSpan={defaultColSpan} isLoading={isLoading}>
						<StringInput name="shippingTerm" disabled={mode === 'view'} />
					</FormItem>

					<FormItem label="freightTerm" colSpan={defaultColSpan} isLoading={isLoading}>
						<StringInput name="freightTerm" disabled={mode === 'view'} />
					</FormItem>

					<FormItem label="paymentTerm" colSpan={defaultColSpan} isLoading={isLoading}>
						<StringInput name="paymentTerm" disabled={mode === 'view'} />
					</FormItem>

					<FormItem>
						<Typography variant="p2">items</Typography>
						<PurchaseOrderItems
							formMethods={formMethods}
							formMode={mode}
							isLoading={isLoading}
							orderID={Number(id)}
						/>
					</FormItem>

					<FormItem label="taxTotal" colSpan={defaultColSpan} isLoading={isLoading}>
						<IntegerInput name="taxTotal" disabled={mode === 'view'} />
					</FormItem>

					<FormItem label="totalQuantity" colSpan={defaultColSpan} isLoading={isLoading}>
						<IntegerInput value={totalQuantityValue} disabled />
					</FormItem>

					<FormItem label="discount" colSpan={defaultColSpan} isLoading={isLoading}>
						<IntegerInput name="discount" disabled={mode === 'view'} />
					</FormItem>

					<FormItem label="subtotal" colSpan={defaultColSpan} isLoading={isLoading}>
						<IntegerInput value={subTotalValue} disabled />
					</FormItem>

					<FormItem label="shipping" colSpan={defaultColSpan} isLoading={isLoading}>
						<IntegerInput name="shipping" disabled={mode === 'view'} />
					</FormItem>

					<FormItem label="total" colSpan={defaultColSpan} isLoading={isLoading}>
						<IntegerInput value={totalValue} disabled />
					</FormItem>

					<FormItem label="miscCharges" colSpan={defaultColSpan} isLoading={isLoading}>
						<IntegerInput name="miscCharges" disabled={mode === 'view'} />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="status" isLoading={isLoading}>
						<Status name="status" value={watch('status')} />
					</FormItem>

					<FormItem label="notes" isLoading={isLoading}>
						<TextArea name="notes" disabled={mode === 'view'} />
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button label="cancel" variant="outline" onClick={() => closeModal() || modal.remove()} />
				{mode !== 'view' && (
					<Button
						label={mode === 'edit' ? 'save' : 'add'}
						disabled={!isDirty || isLoading}
						disabledTooltip={!isDirty && 'formInfo.noChanges'}
						onClick={handleSubmit(onSubmit, (errors) => {
							AppLogger.log(errors);
						})}
					/>
				)}
			</ModalActions>
		</Modal>
	);
});

export default PurchaseOrderForm;
